import { useQuery } from "@tanstack/react-query";
import { api, money } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { Users, UserCircleCheck, Briefcase, Timer, Receipt, WarningCircle, ChartBar } from "@phosphor-icons/react";

const AXIS = { fontSize: 11, fill: "#94a3b8" };

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <Card className="p-5 bg-card border-border" data-testid={`stat-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="overline text-muted-foreground">{label}</span>
        <div className={`h-8 w-8 rounded-md flex items-center justify-center ${accent}`}><Icon className="h-4 w-4" weight="bold" /></div>
      </div>
      <p className="text-2xl font-black">{value}</p>
    </Card>
  );
}

export default function AdminAnalytics() {
  const { data } = useQuery({
    queryKey: ["admin-analytics"],
    queryFn: async () => (await api.get("/admin/analytics")).data,
  });
  if (!data) return <div className="text-muted-foreground animate-pulse">Loading…</div>;

  return (
    <div data-testid="admin-analytics-page">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Stat icon={Users} label="Total Users" value={data.total_users} accent="bg-primary/20 text-primary" />
        <Stat icon={UserCircleCheck} label="Active 30d" value={data.active_users} accent="bg-emerald-500/20 text-emerald-400" />
        <Stat icon={Briefcase} label="Paid Duties" value={data.total_paid_duty} accent="bg-blue-500/20 text-blue-400" />
        <Stat icon={Timer} label="Overtime Hrs" value={data.total_overtime_hours} accent="bg-amber-500/20 text-amber-400" />
        <Stat icon={Receipt} label="Paycheques" value={data.total_paycheques} accent="bg-blue-500/20 text-blue-400" />
        <Stat icon={WarningCircle} label="Underpay Alerts" value={data.underpayment_alerts} accent="bg-destructive/20 text-destructive" />
        <Stat icon={ChartBar} label="Avg Duty Value" value={money(data.avg_paid_duty_value, data.currency)} accent="bg-emerald-500/20 text-emerald-400" />
      </div>
      <Card className="p-5 bg-card border-border" data-testid="user-growth-chart">
        <h3 className="text-base font-bold mb-4">User Growth — Last 12 Months</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.user_growth} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
              <defs>
                <linearGradient id="gU" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
              <XAxis dataKey="month" tick={AXIS} axisLine={false} tickLine={false} />
              <YAxis tick={AXIS} axisLine={false} tickLine={false} width={44} allowDecimals={false} />
              <Tooltip cursor={{ fill: "#1e293b55" }} contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }} />
              <Area type="monotone" dataKey="users" stroke="#3b82f6" strokeWidth={2} fill="url(#gU)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}
